/**
 * Fallacy Data Helpers
 * Pure functions for processing fallacy data
 */

import { FALLACY_DEFINITIONS, type FallacyDefinition } from '@/constants/fallacyDefinitions';
import { UI_CONFIG } from '@/constants/ui';

/**
 * Get similar fallacies in the same category
 */
export const getSimilarFallacies = (
  fallacyType: string,
  limit: number = UI_CONFIG.limits.similarFallaciesCount
): [string, FallacyDefinition][] => {
  const currentFallacy = FALLACY_DEFINITIONS[fallacyType];
  if (!currentFallacy) return [];

  return Object.entries(FALLACY_DEFINITIONS)
    .filter(([key, def]) => 
      key !== fallacyType && 
      def.category === currentFallacy.category
    )
    .slice(0, limit);
};

/**
 * Get preview of fallacy examples
 */
export const getExamplePreview = (
  examples: string[],
  limit: number = UI_CONFIG.limits.fallacyExamplesPreview
): string[] => {
  return examples.slice(0, limit);
};

/**
 * Get preview of alternative names
 */
export const getAlternativeNamesPreview = (
  names: string[],
  limit: number = UI_CONFIG.limits.alternativeNamesPreview
): { preview: string[]; hasMore: boolean; remaining: number } => {
  const preview = names.slice(0, limit);
  const hasMore = names.length > limit;
  const remaining = names.length - limit;

  return { preview, hasMore, remaining };
};

/**
 * Format count text
 */
export const formatCountText = (template: string, values: Record<string, number>): string => {
  return Object.entries(values).reduce(
    (text, [key, value]) => text.replace(`{${key}}`, String(value)),
    template
  );
};
